/**
 * "Search" style tile. Not that it isn't just used on the search results page,
 * but also search-ish places such as destination product pages.
 */
import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import BedbankSearchTile from 'components/SearchKit/SearchTiles/accommodations/BedbankSearchTile'
import GroupedBedbankSearchTile from 'components/SearchKit/SearchTiles/accommodations/GroupedBedbankSearchTile'
import GroupedHotelSearchTile from 'components/SearchKit/SearchTiles/accommodations/GroupedHotelSearchTile'
import HotelBundleSearchTile from 'components/SearchKit/SearchTiles/accommodations/HotelBundleSearchTile'
import VillaSearchTile from 'components/SearchKit/SearchTiles/accommodations/VillaSearchTile'
import CruiseOfferTile from 'components/SearchKit/SearchTiles/cruises/CruiseOfferTile'
import TourV2SearchTile from 'components/SearchKit/SearchTiles/tours/TourV2SearchTile'
import {
  OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import useMetasearchAccountType from 'hooks/useMetasearchAccountType'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import { getGhaPriceMatchForProperty } from 'selectors/ghaPriceMatchSelectors'
import { useGhaDisplayedPriceParams } from 'hooks/useGhaVerifierPrice'
import noop from 'lib/function/noop'
import { isBedbankOffer, isBundleOffer, isCruiseOffer, isCruiseV1Offer, isLEHotel, isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { buildOfferTileUrlFilters } from 'lib/metasearch/metaSearchOfferListFilters'
import { forwardRef, useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import type { TopLevelTileProps } from '../OfferTileTypes'
import { useOfferBestPrice } from 'hooks/Offers/useOfferBestPrice'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import BedbankMetaSearchTile from 'components/SearchKit/SearchTiles/accommodations/BedbankMetaSearchTile'
import { OfferUrlProvider } from 'contexts/OfferUrlContext'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import { hasPassedHidePricing, hasPassedWalledGarden } from 'selectors/accountSelectors'
import { useShouldShowOfferDescription } from 'lib/offer/offerDescriptionUtils'
import HotelSearchTile from '../../../SearchKit/SearchTiles/accommodations/HotelSearchTile'
import { shouldUseGroupedAccommodationSearchTile } from '../../../SearchKit/SearchTiles/accommodations/shouldUseGroupedAccommodationSearchTile'
import getOfferListKey from 'lib/offer/offerListKey'
import { getAccommodationSearchPrimaryAction, getAccommodationSearchProductType, shouldForceAccommodationDateSelection } from 'lib/search/accommodationSearchPresentation'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
const OfferLink = styled(Clickable)`
  position: relative;
`

// Mirrors each tile's CTA-visibility gating, or the outer tile forces non-navigation with no CTA to
// catch the tap. walledGardenBypassed is account state (WalledContent bypasses walledGarden for
// logged-in/previewMode/bypass viewers); Villa treats undefined metadata as unavailable, hotel/bedbank only when getShouldUseSearchPrices is true.
function canOfferPromptForDates(
  offer: App.AnyOffer,
  offerMetaData: App.OfferListMetaData | undefined,
  filters: App.OfferListFilters | undefined,
  walledGardenBypassed: boolean,
): offer is App.BedbankOffer | App.BedbankOfferSummary | App.HotelOffer | App.HotelOfferSummary | App.VillaOffer {
  if (!isBedbankOffer(offer) && !isLEHotel(offer) && !isVillaOffer(offer)) return false
  if (offer.walledGarden && !walledGardenBypassed) return false
  if (offerMetaData?.extendedAvailability) return false

  if (isVillaOffer(offer)) return offerMetaData?.available === true
  if (getShouldUseSearchPrices(filters) && offerMetaData?.available === false) return false
  return true
}

function getTestId(offer: App.AnyOffer) {
  let flightsEnabled
  let onlinePurchaseSchedule

  if (offer.type !== OFFER_TYPE_BED_BANK) {
    const internalOffer = offer as App.Offer

    flightsEnabled = internalOffer.offerFlightsEnabled
    onlinePurchaseSchedule = internalOffer.onlinePurchaseSchedule
  }

  return clsx(
    `offer-${offer.type}`, {
      'offer-with-flights': flightsEnabled,
      [`Purchasable-${offer.type}`]: scheduleIsCurrent(onlinePurchaseSchedule),
    })
}

interface Props extends TopLevelTileProps {
  className?: string
  position?: number
  preventNavigationToOfferPage?: boolean
  storeOfferSearchTile?: boolean
}

const OfferSearchTile = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    offer,
    filters,
    productClick = noop,
    onAdditionalProductImpressionChange = noop,
    className,
    metaSearchResult,
    accommodationId,
    showPerNightPricing = false,
    preventNavigationToOfferPage = false,
    storeOfferSearchTile = false,
    searchTileDensity = 'default',
    searchTileTreatment = 'default',
    position,
    eagerLoadFirstImage,
  } = props
  const isMapCard = searchTileDensity === 'map-card'
  const isMetaSearch = !!metaSearchResult
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()

  // GR-4746 — drop URL dates when no package matches the requested duration
  // (otherwise the destination offer page hangs on a stuck rate fetch).
  const urlFilters = useMemo(
    () => buildOfferTileUrlFilters(filters, offer),
    [filters, offer],
  )

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const productType = getAccommodationSearchProductType(
    offer.productType,
    isLEHotel(offer) && isLPCTacticalEnabled() && !!offer.hasTactical,
  )
  const hidePrice = useHideAnytimePrices(offer.type, !!offerMetaData?.hidePricing, productType, filters, offerMetaData?.accommodationId)

  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  const isAnytimeSearch = !filters?.checkIn && !filters?.checkOut && !filters?.flexibleNights

  const passedWalledGarden = useAppSelector(hasPassedWalledGarden)
  const passedHidePricing = useAppSelector(hasPassedHidePricing)
  const walledGardenBypassed = passedWalledGarden || passedHidePricing

  const dateSelectionInput = {
    offerType: offer.type,
    propertyType: isBedbankOffer(offer) ? offer.propertyType : undefined,
    isAnytimeSearch,
    canSelectDates: !!onAnyHotelSelectDatesPrompt,
  }
  const shouldForceDateSelectionOnClick = canOfferPromptForDates(offer, offerMetaData, filters, walledGardenBypassed) && (
    isAnytimeSearch ?
        shouldForceAccommodationDateSelection(dateSelectionInput) :
      getAccommodationSearchPrimaryAction({
        ...dateSelectionInput,
        hidePrice,
        tileKind: isLEHotel(offer) && !!offerMetaData?.secondaryOfferId ? 'grouped' : 'standard',
      }) === 'select-dates'
  )

  const offerListKey = useMemo(() => filters ? getOfferListKey(filters) : undefined, [filters])
  const searchOfferList = useAppSelector(state => offerListKey ? state.offer.offerLists[offerListKey] : undefined)
  const descriptionLoading = searchOfferList?.descriptionLoading
  const showDescription = useShouldShowOfferDescription(position, descriptionLoading)

  const utmBp = useAppSelector(state => state.utm.bp)
  const utmContent = useAppSelector(state => state.utm.content)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const utmCampaign = useAppSelector(state => state.utm.campaign)
  const ruleId = useAppSelector(state => state.utm.ruleId)
  const gpid = useMetasearchPriceId()
  const accountType = useMetasearchAccountType()
  const ghaPriceMatch = useAppSelector(state => getGhaPriceMatchForProperty(state, offer.id))
  // Not persisted to utm state, so read from the query to forward onto the /partner page.
  const priceTotal = useAppSelector(state => state.router.location.query.priceTotal)
  const ghaDisplayedPrice = useGhaDisplayedPriceParams()
  // GHA price scraper / Hotel Center price-check sessions land with priceCheck=true; forward it
  // onto the /partner page when present so the same metasearchPriceVerifier source is used there.
  const priceCheck = useAppSelector(state => state.router.location.query.priceCheck)
  const isDatedMetasearchSecondaryOffer = !!(filters?.showDistanceWithMetaOfferId && filters.checkIn && filters.checkOut)
  const shouldPropagateMetasearchContext = isMetaSearch || isDatedMetasearchSecondaryOffer
  const shouldIncludeGhaPrice = isMetaSearch

  const offerUrlFilters = useMemo(() => {
    if (!isMetaSearch) return urlFilters
    if (offerMetaData?.kind !== 'bedbank') return urlFilters

    // For GHA LPP click-throughs, prefer the concrete bedbank rate shown on the tile.
    const lowestPriceRoomRateId = offerMetaData?.pricing.lowestPriceRoomRateId
    const lowestPriceRoomRateGroupId = offerMetaData?.pricing.lowestPriceRoomRateGroupId
    const roomTypeId = lowestPriceRoomRateId?.split('|')[1]

    if (!lowestPriceRoomRateId || !roomTypeId) return urlFilters

    return {
      ...urlFilters,
      roomRateId: lowestPriceRoomRateId,
      roomRateGroupId: lowestPriceRoomRateGroupId,
      roomTypeId,
    }
  }, [
    isMetaSearch,
    urlFilters,
    offerMetaData?.kind,
    offerMetaData?.pricing.lowestPriceRoomRateId,
    offerMetaData?.pricing.lowestPriceRoomRateGroupId,
  ])

  const offerUrl = useOfferUrl(offer, {
    filters: offerUrlFilters,
    bundledOfferId: offerMetaData?.bundledOfferId,
    offerLinkIncludesFilters: true,
    searchId: searchOfferList?.searchId,
    showDatePicker: hidePrice,
    includeGhaPrice: shouldIncludeGhaPrice,
    priceCheck: shouldIncludeGhaPrice ? priceCheck : undefined,
    utmBp: shouldPropagateMetasearchContext ? utmBp : undefined,
    utmContent: shouldPropagateMetasearchContext ? utmContent : undefined,
    utmSource: shouldPropagateMetasearchContext ? utmSource : undefined,
    utmMedium: shouldPropagateMetasearchContext ? utmMedium : undefined,
    utmCampaign: shouldPropagateMetasearchContext ? utmCampaign : undefined,
    ruleId: shouldPropagateMetasearchContext ? ruleId : undefined,
    gpid: shouldPropagateMetasearchContext ? gpid : undefined,
    accountType: shouldPropagateMetasearchContext ? accountType : undefined,
    ghaAdvertisedRoom: shouldPropagateMetasearchContext ? ghaPriceMatch : undefined,
    priceTotal: shouldPropagateMetasearchContext ? priceTotal : undefined,
    // Only the searched property — a secondary tile links to a different hotel, which Google's
    // figures do not describe.
    ghaDisplayedPrice: metaSearchResult === 'primary' ? ghaDisplayedPrice : undefined,
    // Only the metasearch main property tile is the growth_metasearch_lpp_promo
    // experiment's entry point — secondary metasearch tiles must not carry the signal.
    metasearchTopTileEntry: metaSearchResult === 'primary',
  })
  useOfferBestPrice(offer, filters, { disabled: getShouldUseSearchPrices(filters) })

  const handleClick = useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
    // The tile's own "Select Dates" CTA (e.g. BedbankSearchTileAction/HotelSearchTileAction)
    // already calls onAnyHotelSelectDatesPrompt itself and calls e.preventDefault() before
    // this click bubbles up here — skip so we don't fire the prompt a second time.
    // Type-narrows offer for onAnyHotelSelectDatesPrompt below — not an independent runtime gate.
    if (shouldForceDateSelectionOnClick && onAnyHotelSelectDatesPrompt && !e?.defaultPrevented && (isBedbankOffer(offer) || isLEHotel(offer) || isVillaOffer(offer))) {
      e?.preventDefault()
      e?.stopPropagation()
      onAnyHotelSelectDatesPrompt(offer)
      productClick?.(offer)
      return
    }

    if (preventNavigationToOfferPage && e) {
      e.preventDefault()
      e.stopPropagation()
    }
    productClick?.(offer)
  }, [offer, productClick, preventNavigationToOfferPage, shouldForceDateSelectionOnClick, onAnyHotelSelectDatesPrompt])

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    if (isMapCard && !(event.target as HTMLElement).closest('[data-map-offer-link]')) return
    productClick?.(offer, true)
  }, [offer, productClick, isMapCard])

  const preventNavigation = preventNavigationToOfferPage || shouldForceDateSelectionOnClick

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(e)
    }
  }, [handleClick])

  // Compact density intentionally collapses grouped secondary options into the primary row.
  const shouldUseGroupedHotelTile = shouldUseGroupedAccommodationSearchTile({
    density: searchTileDensity,
    hasSecondaryOffer: !!offerMetaData?.secondaryOfferId,
  })
  const shouldUseGroupedBedbankTile = shouldUseGroupedAccommodationSearchTile({
    density: searchTileDensity,
    hasSecondaryOffer: !!offerMetaData?.secondaryOfferId,
    isMetaSearch,
  })

  const handleMapCardClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (event.defaultPrevented) return

    const target = event.target as HTMLElement
    if (target.closest('[data-map-offer-link]')) {
      handleClick(event)
    } else if (!target.closest('a, button, input, select, textarea, [role="button"], [role="link"]')) {
      event.currentTarget.querySelector<HTMLAnchorElement>('[data-map-offer-link]')?.click()
    }
  }, [handleClick])

  return <ProductPaletteProvider product={offer}>
    <OfferUrlProvider url={offerUrl}>
      <OfferLink
        onClick={isMapCard ? handleMapCardClick : handleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={preventNavigation && !isMapCard ? handleKeyDown : undefined}
        to={preventNavigation || isMapCard ? undefined : offerUrl}
        as={preventNavigation || isMapCard ? 'div' : undefined}
        role={preventNavigation && !isMapCard ? 'button' : undefined}
        tabIndex={preventNavigation && !isMapCard ? 0 : undefined}
        data-testid={getTestId(offer)}
        target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
        className={className}
        ref={ref}
      >
        {isLEHotel(offer) && shouldUseGroupedHotelTile && <GroupedHotelSearchTile
          offer={offer}
          filters={filters}
          productClick={productClick}
          onAdditionalProductImpressionChange={onAdditionalProductImpressionChange}
          showPerNightPricing={showPerNightPricing}
          storeSearchTile={storeOfferSearchTile}
          showDescription={showDescription}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isLEHotel(offer) && !shouldUseGroupedHotelTile && <HotelSearchTile
          offer={offer}
          filters={filters}
          showPerNightPricing={showPerNightPricing}
          storeSearchTile={storeOfferSearchTile}
          showDescription={showDescription}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isTourV2Offer(offer) && <TourV2SearchTile
          offer={offer}
          filters={filters}
        />}
        {isBedbankOffer(offer) && shouldUseGroupedBedbankTile && <GroupedBedbankSearchTile
          offer={offer}
          filters={filters}
          productClick={productClick}
          onAdditionalProductImpressionChange={onAdditionalProductImpressionChange}
          showDescription={showDescription}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isBedbankOffer(offer) && !shouldUseGroupedBedbankTile && !isMetaSearch && <BedbankSearchTile
          offer={offer}
          filters={filters}
          showDescription={showDescription}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isBedbankOffer(offer) && metaSearchResult && <BedbankMetaSearchTile
          offer={offer}
          filters={filters}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
          metaSearchResult={metaSearchResult}
          accommodationId={accommodationId}
        />}
        {isVillaOffer(offer) && <VillaSearchTile
          offer={offer}
          filters={filters}
          showDescription={showDescription}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {(isCruiseV1Offer(offer) || isCruiseOffer(offer)) && <CruiseOfferTile
          offer={offer}
          filters={filters}
        />}
        {isBundleOffer(offer) && <HotelBundleSearchTile
          density={searchTileDensity}
          offer={offer}
          filters={filters}
          showDescription={showDescription}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />}
      </OfferLink>
    </OfferUrlProvider>
  </ProductPaletteProvider>
})

export default OfferSearchTile
